/**
 * Authentication Redux Slice
 * 
 * Manages authentication state including user information, login status,
 * and loading states. Handles login/logout actions and maintains user
 * session data throughout the application.
 */

import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { User } from '../types';

/**
 * Authentication State Interface
 * 
 * Defines the structure of authentication-related state
 */
interface AuthState {
  user: User | null;          // Current logged-in user information
  isAuthenticated: boolean;   // Whether user is currently authenticated
  isLoading: boolean;         // Loading state for auth operations
}

/**
 * Initial state for authentication
 * 
 * Default state when the application starts
 */
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to check for existing session
};

/**
 * Authentication slice with reducers for managing auth state
 * 
 * Contains all the actions and reducers needed for authentication flow
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Start login process
     * Sets loading state to true during authentication
     */
    loginStart: (state) => {
      state.isLoading = true;
    },
    
    /**
     * Handle successful login
     * Updates user data and authentication status
     * 
     * @param action - Contains user data from successful login
     */
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
    },
    
    /**
     * Handle failed login
     * Resets user data and authentication status
     */
    loginFailure: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
    },
    
    /**
     * Handle user logout
     * Clears all user data and resets authentication state
     */
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
    },
    
    /**
     * Update loading state
     * Used for managing loading indicators during auth operations
     * 
     * @param action - Boolean value for loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

// Export action creators for use in components
export const { loginStart, loginSuccess, loginFailure, logout, setLoading } = authSlice.actions;

// Export reducer for store configuration
export default authSlice.reducer;
